import React, { useEffect, useState } from 'react';
import { AppState } from '../../store/state';
import { selectBidState, selectConference, selectPaperStatusState, selectScreenState } from '../../store/selectors';
import { AppDispatch } from '../../store/store';
import Loading from '../../components/Loading/Loading';
import { connect } from 'react-redux';
import styled, { ThemeProvider } from 'styled-components';
import useTheme from '@material-ui/core/styles/useTheme';
import FileUploadUpdater from '../FileUploadUpdater/FileUploadUpdater';
import { postSubsetBids } from '../../store/bid/sagas';
import { PaperStatus } from '../../store/paper-status/types';
import { BidOption } from '../../store/bid/types';
import BidOptionsManager from '../BidOptionsManager/BidOptionsManager';
import PaperStatusPicker from '../PaperStatusPicker/PaperStatusPicker';
import { useTranslation } from 'react-i18next';
import { Switch } from '@progress/kendo-react-inputs';
import { Tabs, TabsItem } from '../ui';

type SectionName = 'Subset bidding' | 'Paper status' | 'Bid options';

interface Props {
  loading: boolean;
  roleId: number;
  screenByName: { Discussion: Screen; 'Subset Bidding': { [key: number]: Screen } };
  paperStatusById: { [key: number]: PaperStatus };
  paperStatusRole: { [key: number]: number[] };
  bidOptionsById: { [key: number]: BidOption };
  onChange?: (settings: BidSettings) => void;
  disableSaveHandler?: (disabled: boolean) => void;
}

const PhaseSettingsBidding: React.FC<Props> = ({
  loading,
  roleId,
  screenByName,
  paperStatusById,
  paperStatusRole,
  bidOptionsById,
  onChange,
  disableSaveHandler,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const initSettings = (): BidSettings => {
    return {
      subset_bidding_enabled: screenByName[`Subset Bidding`][roleId].settings.enabled,
      subset_bidding_rows: [],
      paper_status_ids: Object.values(paperStatusById)
        .filter((paperStatus) => paperStatus.category != 'rejection')
        .filter((paperStatus) =>
          paperStatusRole[paperStatus.id] ? paperStatusRole[paperStatus.id].includes(roleId) : false,
        )
        .map((paperStatus) => paperStatus.id),
      // Filter bid options by role
      bid_options: Object.values(bidOptionsById).filter((value) => value.role_id === roleId),
    };
  };

  const [settings, setSettings] = useState<BidSettings>(initSettings);
  const [section, setSection] = React.useState<SectionName>('Bid options');

  useEffect(() => {
    onChange?.(settings); // Just a silly way of take out settings to outside at the very beginning
  }, []);

  const settingsChangeHandler = (settings: BidSettings) => {
    setSettings(settings);
    onChange?.(settings); // Call external callback
  };

  if (loading) {
    return <Loading />;
  }

  let bidding;
  switch (section) {
    case 'Subset bidding':
      bidding = (
        <div className="mt-5 ml-4">
          <div className="flex items-center gap-x-2">
            <Switch
              onLabel=""
              offLabel=""
              checked={settings.subset_bidding_enabled}
              onChange={(e) =>
                settingsChangeHandler({ ...settings, subset_bidding_enabled: !settings.subset_bidding_enabled })
              }
            />
            <div>
              <span>{t('Enable subset bidding')}</span>
            </div>
          </div>
          {settings.subset_bidding_enabled && (
            <div className="mt-3">
              <FileUploadUpdater
                doPreview={(file) => postSubsetBids({ file, dry_run: true })}
                onFeedbackHandler={(data) => {
                  const rows = data.data.rows.filter((value) => !value.has_error).map((value) => value.values);
                  settingsChangeHandler({ ...settings, subset_bidding_rows: rows });
                }}
                templateName="subset-bidding"
              />
            </div>
          )}
        </div>
      );
      break;
    case 'Paper status':
      bidding = (
        <PaperStatusPicker
          paperStatusIds={settings.paper_status_ids}
          paperStatusById={paperStatusById}
          onChange={(paperStatusIds) => settingsChangeHandler({ ...settings, paper_status_ids: paperStatusIds })}
          phaseType="bidding"
        />
      );
      break;
    case 'Bid options':
      bidding = (
        <BidOptionsManager
          bidOptions={settings.bid_options}
          onChange={(bidOptions) => settingsChangeHandler({ ...settings, bid_options: bidOptions })}
          disableSaveHandler={disableSaveHandler}
        />
      );
      break;
  }

  return (
    <ThemeProvider theme={theme}>
      <Tabs className="mt-5">
        <TabsItem
          label={t('Bid options')}
          selected={section == 'Bid options'}
          onClick={() => setSection('Bid options')}
        />
        <TabsItem
          label={t('Paper status')}
          selected={section == 'Paper status'}
          onClick={() => setSection('Paper status')}
        />
        <TabsItem
          label={t('Subset bidding')}
          selected={section == 'Subset bidding'}
          onClick={() => setSection('Subset bidding')}
        />
      </Tabs>
      <StyledWrapper className="text-sm max-w-4xl" style={{ width: '33rem' }}>
        <div className="mb-5">{bidding}</div>
      </StyledWrapper>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectConference(state).loading,
  screenByName: selectScreenState(state).screenByName,
  paperStatusById: selectPaperStatusState(state).paperStatusById,
  paperStatusRole: selectPaperStatusState(state).paperStatusRole,
  bidOptionsById: selectBidState(state).bidOptions.byId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PhaseSettingsBidding);

const StyledWrapper = styled.div`
  .minMax {
    input {
      text-align: center;
      padding: 6px 0;
    }
  }

  .addButton {
    margin: 0 auto 0 0.5rem;
  }

  .disabled {
    opacity: 0.5;
  }
`;
